import MasterData from './MasterData';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Plus, Trash2, Download, Upload, RotateCcw, Boxes, Pencil, Check, QrCode, ScanLine , Table2 } from 'lucide-react';
import CatalogTable, { TemplateLoader } from './CatalogTable';
import CatalogShare from './CatalogShare';
import CatalogScan from './CatalogScan';
import { kritisOn } from '../lib/policy';
import { openMfr } from './MfrDialog';
import useCatalog from '../hooks/useCatalog';
import { CATS, SECTORS, UNITS, saveCatalog, factoryCatalog, importCatalog, matchItems, inSector } from '../lib/catalog';
import { download } from '../lib/exporters';
import { getMode } from '../lib/profiles';
import { askConfirm } from '../lib/askConfirm';
import { eur } from '../lib/license';

const TYPE_OF = { valve: 'valve', hydrant: 'hydrant', house: 'house', shaft: 'manhole', sewer: 'manhole' };
const PIPE_OF = { sewer: 'sewer', gas: 'gas', cable: 'power', fiber: 'telecom' };

function ItemForm({ t, item, onSave, onCancel }) {
  const [x, setX] = useState(item);
  const f = (k) => (e) => setX({ ...x, [k]: e.target.value });
  const n = (k) => (e) => setX({ ...x, [k]: e.target.value === '' ? null : +e.target.value });
  return (
    <div className="cat-form" data-testid="cat-form">
      <label>{t.catCat}<select data-testid="cat-form-cat" value={x.cat} onChange={f('cat')}>{CATS.map((c) => <option key={c} value={c}>{t['cat_' + c]}</option>)}</select></label>
      <label>{t.catName}<input data-testid="cat-form-name" value={x.name} onChange={f('name')} /></label>
      <label>{t.catDim}<input data-testid="cat-form-dim" value={x.dim} onChange={f('dim')} /></label>
      <label>{t.catSku}<input data-testid="cat-form-sku" value={x.sku} onChange={f('sku')} /></label>
      <label>{t.catUnit}<select data-testid="cat-form-unit" value={x.unit} onChange={f('unit')}>{UNITS.map((u) => <option key={u} value={u}>{t['unit_' + u]}</option>)}</select></label>
      <label>{t.catKind}<select data-testid="cat-form-kind" value={x.kind} onChange={f('kind')}><option value="point">{t.kind_point}</option><option value="pipe">{t.kind_pipe}</option></select></label>
      <label>{t.catPrice}<input type="number" step="0.01" data-testid="cat-form-price" value={x.price ?? ''} onChange={n('price')} /></label>
      <label>{t.catInstall}<input type="number" step="0.01" data-testid="cat-form-install" value={x.install ?? ''} onChange={n('install')} /></label>
      <label>{t.catColor}<input type="color" data-testid="cat-form-color" value={x.color} onChange={f('color')} /></label>
      <label>{t.catSym}<input data-testid="cat-form-sym" maxLength={3} value={x.sym} onChange={f('sym')} /></label>
      <div className="row">
        <button className="btn btn-green btn-sm" data-testid="cat-form-save" disabled={!x.name.trim()} onClick={() => onSave({ ...x, type: x.kind === 'pipe' ? PIPE_OF[x.cat] || (['water', 'gas', 'sewer', 'power', 'telecom'].includes(x.type) ? x.type : 'water') : TYPE_OF[x.cat] || 'other', unit: x.kind === 'pipe' && x.unit === 'pcs' ? 'lfm' : x.unit })}><Check size={14} /> {t.catSave}</button>
        <button className="btn btn-outline btn-sm" data-testid="cat-form-cancel" onClick={onCancel}>{t.cancel2}</button>
      </div>
    </div>
  );
}

export default function CatalogDialog({ t, onClose }) {
  const cat = useCatalog();
  const [q, setQ] = useState('');
  const [fc, setFc] = useState('');
  const [edit, setEdit] = useState(null);
  const [share, setShare] = useState(null);
  const [table, setTable] = useState(() => window.innerWidth >= 1024);
  const fileRef = useRef(null);
  const save = (items) => saveCatalog({ ...cat, items });
  const upsert = (x) => { save(cat.items.some((y) => y.id === x.id) ? cat.items.map((y) => (y.id === x.id ? x : y)) : [...cat.items, x]); setEdit(null); };
  const blank = () => ({ id: `u${Date.now().toString(36)}`, ...(SECTORS.includes(cat.sector) ? { sector: cat.sector } : {}), cat: fc || 'valve', name: '', dim: '', sku: '', unit: 'pcs', kind: 'point', price: null, install: null, color: '#1d6fe8', sym: '' });
  const onImport = async (e) => {
    const file = e.target.files[0]; e.target.value = '';
    if (!file) return;
    try { await importCatalog(file); toast.success(t.catImported); } catch { toast.error(t.catImportErr); }
  };
  const secItems = cat.items.filter((x) => inSector(x, cat.sector)), list = matchItems(secItems, q, fc);
  const cats = CATS.filter((c) => secItems.some((x) => x.cat === c)), noMfr = ['building', 'garden', 'outdoor'].includes(getMode());
  return createPortal(
    <div className="editor-backdrop" data-testid="catalog-dialog">
      <div className="editor cat-dialog">
        <div className="editor-head">
          <h4><Boxes size={18} /> {t.mdDialog}</h4>
          <button className="icon-btn" data-testid="catalog-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <MasterData t={t} />
        <h5 className="md-cat-head"><Boxes size={15} /> {t.catTitle}</h5>
        <label className="cat-sector">{t.catSector}
          <select data-testid="catalog-sector-select" value={cat.sector} onChange={(e) => saveCatalog({ ...cat, sector: e.target.value })}>
            {[...SECTORS, 'net', 'all'].map((s) => <option key={s} value={s}>{t['sec_' + s]}</option>)}
          </select>
        </label>
        <TemplateLoader t={t} cat={cat} />
        <div className="row cat-tools">
          <input data-testid="catalog-search-input" value={q} placeholder={t.catSearch} onChange={(e) => setQ(e.target.value)} />
          <select data-testid="catalog-cat-filter" value={fc} onChange={(e) => setFc(e.target.value)}>
            <option value="">{`${t.catAll} (${secItems.length})`}</option>
            {cats.map((c) => <option key={c} value={c}>{t['cat_' + c]}</option>)}
          </select>
          <button className={`btn btn-sm ${table ? 'btn-dark' : 'btn-outline'}`} data-testid="catalog-table-toggle" onClick={() => setTable(!table)}><Table2 size={14} /> {t.ctTable}</button>
          <button className="btn btn-dark btn-sm" data-testid="catalog-add-btn" onClick={() => setEdit(blank())}><Plus size={14} /> {t.catAdd}</button>
        </div>
        {edit && !cat.items.some((y) => y.id === edit.id) && <ItemForm t={t} item={edit} onSave={upsert} onCancel={() => setEdit(null)} />}
        {table && <CatalogTable t={t} list={list} items={cat.items} save={save} />}
        {!table && <div className="cat-rows" data-testid="catalog-list">
          {list.map((x) => (edit?.id === x.id ? <ItemForm key={x.id} t={t} item={edit} onSave={upsert} onCancel={() => setEdit(null)} /> : (
            <div key={x.id} className="cat-row" data-testid={`catalog-item-${x.id}`}>
              <i className="fs-dot" style={{ background: x.color }}>{x.sym}</i>
              <span className="cat-row-name"><b>{x.name}</b><small>{[t['cat_' + x.cat], x.dim, x.sku].filter(Boolean).join(' · ')}</small></span>
              <span className="cat-row-price">{x.price != null ? `${eur(x.price, t.locale)}/${t['unit_' + x.unit]}` : '–'}</span>
              <button className="icon-btn" data-testid={`catalog-edit-${x.id}`} onClick={() => setEdit({ ...x })}><Pencil size={14} /></button>
              <button className="icon-btn danger" data-testid={`catalog-delete-${x.id}`} onClick={async () => (await askConfirm(t.catDelConfirm, { cancel: t.btnCancel })) && save(cat.items.filter((y) => y.id !== x.id))}><Trash2 size={14} /></button>
            </div>
          )))}
        </div>}
        <div className="row cat-foot">
          <button className="btn btn-outline btn-sm" data-testid="catalog-export-btn" onClick={() => download(new Blob([JSON.stringify(cat, null, 1)], { type: 'application/json' }), 'GISS-Katalog.json')}><Download size={14} /> {t.catExport}</button>
          <button className="btn btn-outline btn-sm" data-testid="catalog-import-btn" onClick={() => fileRef.current.click()}><Upload size={14} /> {t.catImport}</button>
          <input ref={fileRef} type="file" accept=".json,application/json" hidden data-testid="catalog-import-input" onChange={onImport} />
          {!kritisOn() && <button className="btn btn-proj btn-sm" data-testid="catalog-share-btn" onClick={() => setShare('share')}><QrCode size={14} /> {t.catShare}</button>}
          {!kritisOn() && <button className="btn btn-proj btn-sm" data-testid="catalog-scan-btn" onClick={() => setShare('scan')}><ScanLine size={14} /> {t.catScan}</button>}
          {!noMfr && <button className="btn btn-dark btn-sm" data-testid="catalog-mfr-btn" onClick={openMfr}>🏭 {t.mfTitle}</button>}
          <button className="btn btn-outline btn-sm" data-testid="catalog-reset-btn" onClick={async () => (await askConfirm(t.catResetConfirm, { cancel: t.btnCancel })) && saveCatalog({ sector: cat.sector || 'water', items: factoryCatalog(), cables: 1, v3: 1, v4: 1, v5: 1 })}><RotateCcw size={14} /> {t.catReset}</button>
        </div>
        {share === 'share' && <CatalogShare t={t} onClose={() => setShare(null)} />}
        {share === 'scan' && <CatalogScan t={t} onClose={() => setShare(null)} />}
      </div>
    </div>,
    document.body,
  );
}
